// Billing policy — the ONE definition shared byte-for-byte by the app (src/state/billingStore.ts,
// src/features/settings/PlanPage.tsx), the billing edge function (supabase/functions/billing-v2)
// and the AI quota gate (supabase/functions/_shared/billing/quotaGate.ts). Pure TypeScript: no
// imports, no runtime globals, so it runs in the browser, in Deno and in Node — same discipline as
// aiV2Protocol.ts.
//
// AUTHORITY (do not reinterpret): Owner decisions 2026-10-19 (docs/research/reports/
// 待决事项清单-20261019.md, items 35, 36, 38 and 46). They REPLACE the 2026-09-11 money rules D-3 / D-4 /
// D-5 ("the allowance is 30% of the price"); the line defaults X-2 / X-4 / X-7 stand.
//   P-1  Prices in the apps: single report $14.99 · monthly $99 · annual $999.99 · 「加 3 单包」 $19.99
//        (the 3-inspection pack replaces the $14.99 add-on).
//   P-2  AI is counted in 单 (inspections): monthly includes 20 单 a month, annual 240 单 as ONE
//        yearly pool. A period's 单 expire with the period — nothing rolls over (X-2). The 3-pack is
//        for monthly / annual subscribers only and never expires.
//   P-3  One 单 = one inspection's AI for up to about 400 photos; an inspection beyond that counts as
//        two 单. The inspector sees only 「还剩几单」. The server still prices every AI request in
//        micro-USD and records it (X-7); one 单 carries at most UNIT_CAP_MICROS of AI, which is how
//        "about 400 photos" is measured and what stops one inspection from draining a pool.
//   P-4  The first FREE_TRIAL_INSPECTIONS formal reports per account are free, each carrying one 单.
//   P-5  With no active plan, a formal inspection takes one single purchase, carrying its one 单.
//   P-6  Gift 单 from a redemption code never expire and count like pack 单 (Owner item 46: classmates'
//        five free Demo inspections). Codes are redeemed on the web only — an app never unlocks
//        anything with a code of its own (App Store Review 3.1.1).
//   X-4  Demo inspections are free to create; AI inside a Demo still needs 单.
//   X-7  The allowance is kept and checked on the SERVER before every AI request; the phone shows
//        the server's number and is never the source of truth.
// Owner 2026-10-20 (「1、2、4 和第 3 项都做」 — an unfinished inspection lives on ONE device):
//   V-1  A formal inspection deleted before its final report, with no AI used, gives back its free
//        report / single purchase — at most RELEASE_LIMIT per RELEASE_WINDOW_DAYS.
//   V-2  The server remembers which device an open inspection is on, so the other device can say
//        「别的设备上还有 N 单没做完」; the final report closes it.
//   V-3  A file that carries an inspection to another device moves it there (same inspection, same
//        coverage — moving never charges again).
// Prices are CLOSED: the four price points below are literals that this product never computes with,
// discounts or re-derives.
//
// Money is integer MICRO-USD (1e-6 USD) everywhere. Floating-point dollars drift once they are
// summed over thousands of AI calls; micro-USD keeps every ledger row, every sum and every
// comparison exact, and token cost × price-per-million-tokens lands directly in micro-USD.
export const BILLING_PROTOCOL_VERSION = 1;
export const BILLING_FUNCTION_PATH = '/functions/v1/billing-v2';

// RevenueCat entitlement that carries the subscription (the previous line's project is reused —
// readonly audit 2026-09-08 §1.2; Owner decision 2026-09-11 "RevenueCat 沿用旧线项目").
export const BILLING_ENTITLEMENT_ID = 'pro';

export type BillingProductKey = 'single' | 'monthly' | 'annual' | 'addOn';
export type PlanKind = 'monthly' | 'annual';

// App Store / RevenueCat product identifiers. Same App Store Connect record as the previous line
// (Owner decision 2026-09-11): the Owner confirms the four products still exist at these ids. Since
// 2026-10-19 `addOn` is the 「加 3 单包」 ($19.99) under the same product id; its price and display name
// are changed in App Store Connect and Play Console, not here.
export const BILLING_PRODUCT_IDS: Readonly<Record<BillingProductKey, string>> = Object.freeze({
  single: 'com.nezha2capital.yanwushi.singlereport',
  monthly: 'com.nezha2capital.yanwushi.monthly',
  annual: 'com.nezha2capital.yanwushi.annual',
  addOn: 'com.nezha2capital.yanwushi.aipack10'
});

// The web version (Owner decision list 2026-10-19 item 39): the same four products sold on the web through
// RevenueCat Web Billing (Stripe). Their identifiers are the ones created in RevenueCat for the Web Billing
// app, attached to the same `pro` entitlement, so a web purchase counts on the phone and a store purchase on
// the web (docs/web/WEB-BILLING-SETUP.md). The server treats both sets alike.
export const BILLING_WEB_PRODUCT_IDS: Readonly<Record<BillingProductKey, string>> = Object.freeze({
  single: 'yanwushi_web_single',
  monthly: 'yanwushi_web_monthly',
  annual: 'yanwushi_web_annual',
  addOn: 'yanwushi_web_pack3'
});

export const BILLING_PRODUCT_KEYS: readonly BillingProductKey[] = Object.freeze(['single', 'monthly', 'annual', 'addOn']);

// Which of the four products a store or web product id is (Google Play's `<id>:<basePlan>` included).
export function productKeyOfId(id: string | null): BillingProductKey | null {
  if (!id) return null;
  const bare = id.split(':')[0];
  for (const key of BILLING_PRODUCT_KEYS) if (BILLING_PRODUCT_IDS[key] === bare || BILLING_WEB_PRODUCT_IDS[key] === bare) return key;
  return null;
}

// Subscription products are queried as subscriptions and one-time products as non-subscriptions;
// asking for a one-time product in the subscription catalogue returns an EMPTY list on Google Play
// and is a documented platform trap (memory yanwushi-android-platform). iOS ignores the category,
// so keeping the split costs nothing and keeps the catalogue honest.
export const BILLING_ONE_TIME_KEYS: readonly BillingProductKey[] = Object.freeze(['single', 'addOn']);

export function isOneTimeProduct(key: BillingProductKey): boolean {
  return (BILLING_ONE_TIME_KEYS as readonly string[]).includes(key);
}

// The fixed price points. They are DISPLAY constants: the screen shows these, a promo code never
// changes them (a code grants an entitlement through Apple, it does not discount a displayed
// price), and nothing in this product multiplies, discounts or re-derives them.
export const BILLING_PRICE_LABELS: Readonly<Record<BillingProductKey, string>> = Object.freeze({
  single: '$14.99',
  monthly: '$99',
  annual: '$999.99',
  addOn: '$19.99'
});

// Web prices (item 39): the subscriptions about 5% below the stores, the single report and the pack the same.
export const BILLING_WEB_PRICE_LABELS: Readonly<Record<BillingProductKey, string>> = Object.freeze({
  single: '$14.99',
  monthly: '$94',
  annual: '$949',
  addOn: '$19.99'
});

export const MICROS_PER_USD = 1_000_000;

// P-2: the 单 each plan period includes. Annual is ONE pool for the whole year.
export const PLAN_UNITS: Readonly<Record<PlanKind, number>> = Object.freeze({ monthly: 20, annual: 240 });

// P-1 / P-2: one 「加 3 单包」 adds three 单 (subscribers only, never expires).
export const ADD_ON_UNITS_PER_PACK = 3;

// P-3: the most AI one 单 carries. Measured 2026-10-19 (docs/qa/evidence/ai-8points-2026-10-19/
// after-19e/cost.md): the full chain costs $0.010 a photo for sorting + scanning plus about $0.60 an
// inspection for the report wording and the advanced reviews, so 400 photos ≈ $4.60. $5.00 is that
// with a little room for re-asks. An inspection whose AI passes it opens its second 单.
export const UNIT_CAP_MICROS = 5_000_000;

// P-3, as the inspector reads it: past this many photos an inspection counts as two 单.
export const LARGE_INSPECTION_PHOTOS = 400;

// V-1 (Owner 2026-10-20: 「1、2、4 和第 3 项都做」, the device-scoped workflow): a formal inspection that is
// deleted before its final report, and that never used any AI, gives back the free report or the single
// purchase it took. At most RELEASE_LIMIT of these per RELEASE_WINDOW_DAYS per account, so a crafted call
// cannot turn the free reports into an endless supply. A plan inspection has nothing to give back.
export const RELEASE_LIMIT = 3;
export const RELEASE_WINDOW_DAYS = 30;

// P-4: the ONE constant for free formal reports — three per ACCOUNT, counted server-side, each
// carrying one 单 (Owner 2026-09-11 「头三次免费」, confirmed 2026-10-19 item 38).
export const FREE_TRIAL_INSPECTIONS = 3;

// Every 单 an inspection has opened carries UNIT_CAP_MICROS of AI. How many it needs for what it has
// already used plus the request about to run: never fewer than one.
export function unitsNeededFor(usedMicros: number, reserveMicros: number): number {
  return Math.max(1, Math.ceil((clampPositive(usedMicros) + clampPositive(reserveMicros)) / UNIT_CAP_MICROS));
}

// Where one 单 of an inspection came from. Written once per (inspection, unit number), server-side.
//   coverage  the inspection's own: the single purchase or free report that paid for it (unit 1 only)
//   pool      the current plan period's 单
//   addOn     a 3-pack or a gift (redemption code) 单 — they never expire
//   single    a further single purchase, spent on the second 单 of a large formal inspection
//   trial     a further free report, spent the same way
//   internal  an internal test account
export type AiUnitSource = 'coverage' | 'pool' | 'addOn' | 'single' | 'trial' | 'internal';

export const AI_UNIT_SOURCES: readonly AiUnitSource[] = Object.freeze(['coverage', 'pool', 'addOn', 'single', 'trial', 'internal']);

export function isAiUnitSource(value: unknown): value is AiUnitSource {
  return typeof value === 'string' && (AI_UNIT_SOURCES as readonly string[]).includes(value);
}

// How a formal inspection was paid for. Written once per inspection, server-side, idempotent.
//   plan      an active monthly / annual subscription covered it (unlimited inspections, D-4)
//   single    one single purchase was consumed; the inspection carries its own first 单 (P-5)
//   trial     one of the first FREE_TRIAL_INSPECTIONS free inspections of this account
//   internal  an internal test account (BILLING_INTERNAL_TEST_USER_IDS on the server)
//   unfunded  nothing covered it: reconciliation of an inspection created offline against a credit
//             that was already spent elsewhere. Recorded so it is visible; grants no AI allowance.
export type InspectionCoverage = 'plan' | 'single' | 'trial' | 'unfunded' | 'internal';

export const INSPECTION_COVERAGE_KINDS: readonly InspectionCoverage[] = Object.freeze(['plan', 'single', 'trial', 'unfunded', 'internal']);

export function isInspectionCoverage(value: unknown): value is InspectionCoverage {
  return typeof value === 'string' && (INSPECTION_COVERAGE_KINDS as readonly string[]).includes(value);
}

// Coverage kinds that carry the inspection's own first 单 (P-4 / P-5).
export function coverageCarriesInspectionQuota(coverage: InspectionCoverage | null): boolean {
  return coverage === 'single' || coverage === 'trial';
}

// ---------- what the store told us was bought (RevenueCat is the authority) ----------

export interface BillingPurchase {
  // RevenueCat's transaction id for this one-time purchase (stable, unique per purchase).
  id: string;
  at: number;
  sandbox: boolean;
}

export interface PurchaseFacts {
  plan: PlanKind | null;
  planProductId: string | null;
  // Start of the CURRENT subscription period (the latest purchase / renewal). The period key is
  // built from it, so a renewal starts a fresh pool and nothing rolls over (X-2).
  periodStart: number | null;
  // End of the current period (grace period included when the store granted one).
  activeUntil: number | null;
  willRenew: boolean | null;
  singlePurchases: BillingPurchase[];
  addOnPurchases: BillingPurchase[];
  // The store's own subscription-management page (Apple: apps.apple.com/account/subscriptions).
  managementUrl: string | null;
}

export function emptyPurchaseFacts(): PurchaseFacts {
  return { plan: null, planProductId: null, periodStart: null, activeUntil: null, willRenew: null, singlePurchases: [], addOnPurchases: [], managementUrl: null };
}

export function planActiveAt(facts: PurchaseFacts, at: number): boolean {
  return facts.plan !== null && facts.activeUntil !== null && facts.activeUntil > at;
}

// The identity of the current allowance pool. A renewal changes `periodStart`, so the key changes
// and the new period starts at zero used — "no rollover" is a consequence of the key, not of a
// scheduled job that could fail to run (X-2).
export function periodKeyOf(facts: PurchaseFacts): string | null {
  if (facts.plan === null || facts.periodStart === null) return null;
  return `${facts.planProductId ?? facts.plan}:${facts.periodStart}`;
}

// P-2: the 单 the current plan period includes (0 without an active plan).
export function planUnits(facts: PurchaseFacts, at: number): number {
  return planActiveAt(facts, at) && facts.plan !== null ? PLAN_UNITS[facts.plan] : 0;
}

// P-2 / P-6: the never-expiring 单 — every 3-pack bought plus every gift 单 redeemed.
export function addOnUnits(facts: PurchaseFacts, giftUnits: number): number {
  return facts.addOnPurchases.length * ADD_ON_UNITS_PER_PACK + clampPositive(giftUnits);
}

// ---------- what the ledger says was used / consumed ----------

export interface CoverageRecord {
  inspectionId: string;
  coverage: InspectionCoverage;
  // The single purchase this inspection consumed (null for every other coverage kind).
  transactionId: string | null;
  at: number;
  // V-2 (2026-10-20): the device the open inspection lives on — set when it is created and whenever a file
  // carries it to another device. null = a record from before the rule, or a device that never said.
  holder?: InspectionHolder | null;
  // The final report was generated (the app says so once), or the inspection was deleted on its device
  // without giving anything back. Either way it is no longer open anywhere.
  closedAt?: number | null;
  discardedAt?: number | null;
}

export type HolderPlatform = 'ios' | 'android' | 'web';

// One device, as the inspector would recognise it: its random installation id and a short label
// ("iPhone", "Chrome · Windows"). Never a model number, a name or a location.
export interface InspectionHolder {
  deviceId: string;
  platform: HolderPlatform;
  label: string;
}

export function isHolderPlatform(value: unknown): value is HolderPlatform {
  return value === 'ios' || value === 'android' || value === 'web';
}

// An inspection the account still has open on ANOTHER device (V-2): created there or carried there by a
// file, no final report yet, not deleted there. What the app shows as 「别的设备上还有 N 单没做完」.
export function openElsewhere(coverage: readonly CoverageRecord[], thisDeviceId: string): CoverageRecord[] {
  return coverage.filter((record) => record.holder && record.holder.deviceId !== thisDeviceId && !record.closedAt && !record.discardedAt);
}

export interface LedgerUsage {
  // 单 opened from the CURRENT period's pool.
  poolUnitsUsed: number;
  // 单 opened from 3-packs and gifts, all time (they never expire, X-2).
  addOnUnitsUsed: number;
  // Gift 单 this account redeemed (P-6), all time.
  giftUnits: number;
  // Single purchases spent on the second 单 of a large formal inspection (P-3), and free reports
  // spent the same way. They count against the account's purchases / free reports like coverage.
  extraSingleIds: string[];
  extraTrials: number;
  // 单 each inspection has opened so far, and the AI it has used (micro-USD), by inspection id.
  inspectionUnits: Record<string, number>;
  inspectionUsedMicros: Record<string, number>;
  // V-1: free reports / single purchases given back in the last RELEASE_WINDOW_DAYS.
  releasesRecent: number;
}

export function emptyLedgerUsage(): LedgerUsage {
  return { poolUnitsUsed: 0, addOnUnitsUsed: 0, giftUnits: 0, extraSingleIds: [], extraTrials: 0, inspectionUnits: {}, inspectionUsedMicros: {}, releasesRecent: 0 };
}

function clampPositive(value: number): number {
  return value > 0 ? Math.floor(value) : 0;
}

// Micro-USD → dollars, rounded DOWN to the cent: the number on screen never promises allowance the
// account does not have.
export function usdFromMicros(micros: number): number {
  return Math.floor(clampPositive(micros) / 10_000) / 100;
}

// ---------- the account's billing state (what the app shows and gates on) ----------

export interface BillingStatus {
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  serverTime: number;
  plan: PlanKind | null;
  activeUntil: number | null;
  willRenew: boolean | null;
  periodKey: string | null;
  // P-2: the period's 单 and what is left of them; the never-expiring 单 (packs + gifts) left.
  poolUnits: number;
  poolRemainingUnits: number;
  addOnRemainingUnits: number;
  giftUnits: number;
  // 「还剩几单」: pool + packs + gifts. An inspection's own first 单 (single purchase / free report) is
  // NOT in here; it belongs to that inspection.
  remainingUnits: number;
  // The same number, kept under its earlier name for every screen that reads it.
  remainingInspections: number;
  // remainingUnits × UNIT_CAP_MICROS — an upper bound, kept so "is anything left" stays one check.
  remainingMicros: number;
  // Single purchases the store knows about, how many are already spent, and what is left.
  singlePurchases: number;
  singleConsumed: number;
  singleCredits: number;
  trialRemaining: number;
  internalTester: boolean;
  canBuyAddOn: boolean;
  managementUrl: string | null;
  coverage: CoverageRecord[];
  // 单 each inspection has opened, and the AI it can still use inside them (micro-USD). A single
  // purchase / free report inspection counts its own first 单 even before it is opened.
  inspectionUnits: Record<string, number>;
  inspectionRemainingMicros: Record<string, number>;
  // V-1: how many more deleted inspections can give their free report / single purchase back in the
  // current window. A server from before the rule answers nothing, which reads as RELEASE_LIMIT.
  releasesLeft: number;
}

export interface BillingStatusInput {
  facts: PurchaseFacts;
  coverage: CoverageRecord[];
  usage: LedgerUsage;
  now: number;
  internalTester: boolean;
}

export function computeBillingStatus(input: BillingStatusInput): BillingStatus {
  const { facts, coverage, usage, now } = input;
  const active = planActiveAt(facts, now);
  const poolUnitsNow = planUnits(facts, now);
  const poolRemainingUnits = clampPositive(poolUnitsNow - usage.poolUnitsUsed);
  const addOnRemainingUnits = clampPositive(addOnUnits(facts, usage.giftUnits) - usage.addOnUnitsUsed);
  const singleConsumed = coverage.filter((record) => record.coverage === 'single').length + usage.extraSingleIds.length;
  const trialUsed = coverage.filter((record) => record.coverage === 'trial').length + usage.extraTrials;
  const inspectionRemaining: Record<string, number> = {};
  const owned = new Set(coverage.filter((record) => coverageCarriesInspectionQuota(record.coverage)).map((record) => record.inspectionId));
  for (const id of new Set([...owned, ...Object.keys(usage.inspectionUnits)])) {
    const opened = Math.max(usage.inspectionUnits[id] ?? 0, owned.has(id) ? 1 : 0);
    inspectionRemaining[id] = clampPositive(opened * UNIT_CAP_MICROS - (usage.inspectionUsedMicros[id] ?? 0));
  }
  const remainingUnits = poolRemainingUnits + addOnRemainingUnits;
  return {
    protocolVersion: BILLING_PROTOCOL_VERSION,
    serverTime: now,
    plan: active ? facts.plan : null,
    activeUntil: facts.activeUntil,
    willRenew: facts.willRenew,
    periodKey: active ? periodKeyOf(facts) : null,
    poolUnits: poolUnitsNow,
    poolRemainingUnits,
    addOnRemainingUnits,
    giftUnits: clampPositive(usage.giftUnits),
    remainingUnits,
    remainingInspections: remainingUnits,
    remainingMicros: remainingUnits * UNIT_CAP_MICROS,
    singlePurchases: facts.singlePurchases.length,
    singleConsumed,
    singleCredits: Math.max(0, facts.singlePurchases.length - singleConsumed),
    trialRemaining: Math.max(0, FREE_TRIAL_INSPECTIONS - trialUsed),
    internalTester: input.internalTester,
    // P-2: the 3-pack is offered to active subscribers only. A non-subscriber never sees the button
    // and purchaseAddOn() refuses, so the rule holds even if a screen forgets to hide it.
    canBuyAddOn: active,
    managementUrl: facts.managementUrl,
    coverage,
    inspectionUnits: { ...usage.inspectionUnits },
    inspectionRemainingMicros: inspectionRemaining,
    releasesLeft: Math.max(0, RELEASE_LIMIT - clampPositive(usage.releasesRecent))
  };
}

export function emptyBillingStatus(now: number, internalTester = false): BillingStatus {
  return computeBillingStatus({ facts: emptyPurchaseFacts(), coverage: [], usage: emptyLedgerUsage(), now, internalTester });
}

// ---------- creating a formal inspection ----------

// What the CLIENT is allowed to do right now. Three outcomes only: the inspection may be created,
// a purchase is needed, or the device is offline with nothing left to spend (P-5, X-4).
// 'serverUnavailable' = the ledger could not be reached, so what this account has (free inspections
// and purchases are counted THERE, X-7) is unknown. Never sell into that state: the inspector is told
// to try again, not asked to buy something they may already own.
export type InspectionPermission = 'allowed' | 'needsPurchase' | 'offlineNoCredit' | 'serverUnavailable';

export type InspectionKind = 'formal' | 'demo';

// The coverage the account can offer for ONE more formal inspection, or null when it can offer
// none. Order: internal test account → active plan → a single purchase → a free inspection.
export function coverageAvailable(status: Pick<BillingStatus, 'plan' | 'internalTester' | 'singleCredits' | 'trialRemaining'>): InspectionCoverage | null {
  if (status.internalTester) return 'internal';
  if (status.plan !== null) return 'plan';
  if (status.singleCredits > 0) return 'single';
  if (status.trialRemaining > 0) return 'trial';
  return null;
}

// ---------- what an AI request may draw on ----------

// The arguments of one reservation in the ledger (supabase/migrations/…_billing_ai_units.sql). Since
// 2026-10-19 every request draws only on its inspection's opened 单 (inspectionGrantMicros =
// opened × UNIT_CAP_MICROS); the pool and the packs are spent by OPENING 单, never as dollars, so their
// two dollar grants stay 0. The fields keep the ledger's original signature.
export interface AiGrants {
  inspectionGrantMicros: number;
  poolGrantMicros: number;
  addOnGrantMicros: number;
  periodKey: string | null;
}

export function aiGrantsForUnits(openedUnits: number, periodKey: string | null): AiGrants {
  return { inspectionGrantMicros: clampPositive(openedUnits) * UNIT_CAP_MICROS, poolGrantMicros: 0, addOnGrantMicros: 0, periodKey };
}

export interface ChargeSplit {
  inspectionMicros: number;
  poolMicros: number;
  addOnMicros: number;
  // Cost beyond every allowance. Only reachable when the last request of an allowance cost more
  // than what was left (the check is "is anything left", never "does the worst case fit"), or for
  // an internal test account. Recorded, never hidden.
  excessMicros: number;
}

// Draw order of the ledger's settlement: the inspection's opened 单 first, then the two dollar grants
// (0 since 2026-10-19), then excess.
export function splitCharge(costMicros: number, grants: AiGrants, used: { inspectionMicros: number; poolMicros: number; addOnMicros: number }): ChargeSplit {
  let left = clampPositive(costMicros);
  const takeInspection = Math.min(left, clampPositive(grants.inspectionGrantMicros - used.inspectionMicros));
  left -= takeInspection;
  const takePool = Math.min(left, clampPositive(grants.poolGrantMicros - used.poolMicros));
  left -= takePool;
  const takeAddOn = Math.min(left, clampPositive(grants.addOnGrantMicros - used.addOnMicros));
  left -= takeAddOn;
  return { inspectionMicros: takeInspection, poolMicros: takePool, addOnMicros: takeAddOn, excessMicros: left };
}

// ---------- wire validation (the client trusts nothing it did not validate) ----------

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function numOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function isPlanKind(value: unknown): value is PlanKind {
  return value === 'monthly' || value === 'annual';
}

export function checkHolder(raw: unknown): InspectionHolder | null {
  if (!isRec(raw) || typeof raw.deviceId !== 'string' || !/^[A-Za-z0-9_-]{8,80}$/.test(raw.deviceId) || !isHolderPlatform(raw.platform)) return null;
  return { deviceId: raw.deviceId, platform: raw.platform, label: typeof raw.label === 'string' ? raw.label.slice(0, 60) : '' };
}

export function checkCoverageRecord(raw: unknown): CoverageRecord | null {
  if (!isRec(raw)) return null;
  if (typeof raw.inspectionId !== 'string' || raw.inspectionId.length === 0) return null;
  if (!isInspectionCoverage(raw.coverage)) return null;
  return {
    inspectionId: raw.inspectionId,
    coverage: raw.coverage,
    transactionId: strOrNull(raw.transactionId),
    at: num(raw.at),
    holder: checkHolder(raw.holder),
    closedAt: numOrNull(raw.closedAt),
    discardedAt: numOrNull(raw.discardedAt)
  };
}

// Validates a status object off the wire (or out of the offline cache). Returns null when the
// shape is unusable — the caller then keeps what it had instead of showing invented numbers.
export function checkBillingStatus(raw: unknown): BillingStatus | null {
  if (!isRec(raw) || raw.protocolVersion !== BILLING_PROTOCOL_VERSION) return null;
  const coverage: CoverageRecord[] = [];
  if (Array.isArray(raw.coverage)) {
    for (const entry of raw.coverage) {
      const record = checkCoverageRecord(entry);
      if (record) coverage.push(record);
    }
  }
  const inspectionRemaining: Record<string, number> = {};
  if (isRec(raw.inspectionRemainingMicros)) {
    for (const [key, value] of Object.entries(raw.inspectionRemainingMicros)) inspectionRemaining[key] = clampPositive(num(value));
  }
  const inspectionUnits: Record<string, number> = {};
  if (isRec(raw.inspectionUnits)) {
    for (const [key, value] of Object.entries(raw.inspectionUnits)) inspectionUnits[key] = clampPositive(num(value));
  }
  // A server from before 2026-10-19 answers in dollars only: its number is read the way it was meant
  // then (about $4.50 an inspection) until the server is updated.
  const legacy = typeof raw.remainingUnits !== 'number';
  const remainingUnits = legacy ? Math.floor(clampPositive(num(raw.remainingMicros)) / LEGACY_INSPECTION_ESTIMATE_MICROS) : clampPositive(num(raw.remainingUnits));
  const plan = isPlanKind(raw.plan) ? raw.plan : null;
  return {
    protocolVersion: BILLING_PROTOCOL_VERSION,
    serverTime: num(raw.serverTime),
    plan,
    activeUntil: numOrNull(raw.activeUntil),
    willRenew: typeof raw.willRenew === 'boolean' ? raw.willRenew : null,
    periodKey: strOrNull(raw.periodKey),
    poolUnits: clampPositive(num(raw.poolUnits)),
    poolRemainingUnits: legacy ? remainingUnits : clampPositive(num(raw.poolRemainingUnits)),
    addOnRemainingUnits: clampPositive(num(raw.addOnRemainingUnits)),
    giftUnits: clampPositive(num(raw.giftUnits)),
    remainingUnits,
    remainingInspections: remainingUnits,
    remainingMicros: legacy ? clampPositive(num(raw.remainingMicros)) : remainingUnits * UNIT_CAP_MICROS,
    singlePurchases: clampPositive(num(raw.singlePurchases)),
    singleConsumed: clampPositive(num(raw.singleConsumed)),
    singleCredits: clampPositive(num(raw.singleCredits)),
    trialRemaining: clampPositive(num(raw.trialRemaining)),
    internalTester: raw.internalTester === true,
    canBuyAddOn: raw.canBuyAddOn === true && plan !== null,
    managementUrl: strOrNull(raw.managementUrl),
    coverage,
    inspectionUnits,
    inspectionRemainingMicros: inspectionRemaining,
    releasesLeft: typeof raw.releasesLeft === 'number' ? Math.min(RELEASE_LIMIT, clampPositive(num(raw.releasesLeft))) : RELEASE_LIMIT
  };
}

// The per-inspection estimate the 2026-09-11 server divided its dollars by. Read-only compatibility.
const LEGACY_INSPECTION_ESTIMATE_MICROS = 4_500_000;

// ---------- billing-v2 request / response contract ----------

export type BillingAction = 'status' | 'consume' | 'redeem' | 'release' | 'close' | 'hold';

// P-6: a redemption code turns into gift 单 on this account. Only the web build sends it. `consent` is
// the inspector's tick that the Demo inspections made with these 单 may be used, anonymised, to
// measure AI accuracy (Owner item 46) — a code is never redeemed without it.
export interface BillingRedeemRequest {
  action: 'redeem';
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  code: string;
  consent: true;
}

export type RedeemOutcome = 'redeemed' | 'invalid' | 'expired' | 'alreadyRedeemed' | 'usedUp';

export const REDEEM_OUTCOMES: readonly RedeemOutcome[] = Object.freeze(['redeemed', 'invalid', 'expired', 'alreadyRedeemed', 'usedUp']);

// Codes are typed by people: case and spaces / dashes do not matter. 6–32 letters or digits.
export function normalizeRedeemCode(raw: string): string | null {
  const code = raw.toUpperCase().replace(/[\s-]+/g, '');
  return /^[A-Z0-9]{6,32}$/.test(code) ? code : null;
}

export interface BillingConsumeRequest {
  action: 'consume';
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  inspectionId: string;
  // When the device created the inspection (it may have been offline). Used to honour a plan that
  // was still active at creation time; never trusted beyond the window it can prove.
  clientCreatedAt: number | null;
  offline: boolean;
  // V-2: the device creating it. Recorded only when the inspection has no device yet, so a late replay
  // from an offline phone never takes back an inspection a file has since carried elsewhere. Absent from
  // an app built before the rule.
  device?: InspectionHolder | null;
}

export interface BillingStatusRequest {
  action: 'status';
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
}

// V-1: an unfinished formal inspection was deleted on `device`. The server gives back its free report /
// single purchase when nothing was used (no AI, no report) and the inspection still lives on that device;
// otherwise it only stops counting it as open.
export interface BillingReleaseRequest {
  action: 'release';
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  inspectionId: string;
  device: InspectionHolder;
}

// V-2: the inspection's final report was generated — it is no longer open anywhere.
export interface BillingCloseRequest {
  action: 'close';
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  inspectionId: string;
}

// V-3: a file carried the inspection to `device`, which holds it from now on.
export interface BillingHoldRequest {
  action: 'hold';
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  inspectionId: string;
  device: InspectionHolder;
}

export type BillingRequest = BillingStatusRequest | BillingConsumeRequest | BillingRedeemRequest | BillingReleaseRequest | BillingCloseRequest | BillingHoldRequest;

//   released       the free report / single purchase is back
//   aiUsed         AI ran for it, so what paid for it stays spent
//   reported       its final report exists; nothing to give back
//   heldElsewhere  a file carried it to another device, where it is still open — nothing changes
//   limitReached   RELEASE_LIMIT were already given back in this window
//   notCounted     a plan / internal / unfunded inspection: nothing to give back
//   notFound       the server never recorded it (or already gave it back)
export type ReleaseOutcome = 'released' | 'aiUsed' | 'reported' | 'heldElsewhere' | 'limitReached' | 'notCounted' | 'notFound';

export const RELEASE_OUTCOMES: readonly ReleaseOutcome[] = Object.freeze(['released', 'aiUsed', 'reported', 'heldElsewhere', 'limitReached', 'notCounted', 'notFound']);

export type HoldOutcome = 'held' | 'closed' | 'notFound';

export const HOLD_OUTCOMES: readonly HoldOutcome[] = Object.freeze(['held', 'closed', 'notFound']);

export type BillingErrorCode = 'unauthenticated' | 'badRequest' | 'billingUnavailable' | 'rateLimited' | 'internalError';

export const BILLING_ERROR_STATUS: Readonly<Record<BillingErrorCode, number>> = Object.freeze({
  unauthenticated: 401,
  badRequest: 400,
  billingUnavailable: 503,
  rateLimited: 429,
  internalError: 500
});

export interface BillingOkResponse {
  ok: true;
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  status: BillingStatus;
  // Present on a consume answer: the coverage the SERVER recorded for that inspection.
  coverage?: InspectionCoverage;
  inspectionId?: string;
  // Present on a redeem answer: what happened to the code, and the 单 it gave (0 unless redeemed).
  redeem?: { outcome: RedeemOutcome; units: number };
  // Present on a release / hold / close answer.
  release?: { outcome: ReleaseOutcome; coverage: InspectionCoverage | null };
  hold?: { outcome: HoldOutcome };
  closed?: boolean;
}

export interface BillingErrorResponse {
  ok: false;
  protocolVersion: typeof BILLING_PROTOCOL_VERSION;
  code: BillingErrorCode;
  message: string;
}

export type BillingResponse = BillingOkResponse | BillingErrorResponse;

export function billingError(code: BillingErrorCode, message: string): BillingErrorResponse {
  return { ok: false, protocolVersion: BILLING_PROTOCOL_VERSION, code, message };
}

export function checkBillingRequest(raw: unknown): { ok: true; request: BillingRequest } | { ok: false; code: BillingErrorCode; message: string } {
  if (!isRec(raw)) return { ok: false, code: 'badRequest', message: 'request body is not an object' };
  if (raw.protocolVersion !== BILLING_PROTOCOL_VERSION) return { ok: false, code: 'badRequest', message: `unsupported protocolVersion ${String(raw.protocolVersion)}` };
  if (raw.action === 'status') return { ok: true, request: { action: 'status', protocolVersion: BILLING_PROTOCOL_VERSION } };
  if (raw.action === 'redeem') {
    const code = typeof raw.code === 'string' ? normalizeRedeemCode(raw.code) : null;
    if (!code) return { ok: false, code: 'badRequest', message: 'code missing or malformed' };
    if (raw.consent !== true) return { ok: false, code: 'badRequest', message: 'consent is required' };
    return { ok: true, request: { action: 'redeem', protocolVersion: BILLING_PROTOCOL_VERSION, code, consent: true } };
  }
  if (raw.action !== 'consume' && raw.action !== 'release' && raw.action !== 'close' && raw.action !== 'hold') {
    return { ok: false, code: 'badRequest', message: 'action must be status, consume, redeem, release, close or hold' };
  }
  if (typeof raw.inspectionId !== 'string' || raw.inspectionId.length === 0 || raw.inspectionId.length > 120) {
    return { ok: false, code: 'badRequest', message: 'inspectionId missing' };
  }
  const inspectionId = raw.inspectionId;
  const device = checkHolder(raw.device);
  if (raw.action === 'close') return { ok: true, request: { action: 'close', protocolVersion: BILLING_PROTOCOL_VERSION, inspectionId } };
  if (raw.action === 'release' || raw.action === 'hold') {
    if (!device) return { ok: false, code: 'badRequest', message: 'device missing or malformed' };
    return { ok: true, request: { action: raw.action, protocolVersion: BILLING_PROTOCOL_VERSION, inspectionId, device } };
  }
  return {
    ok: true,
    request: {
      action: 'consume',
      protocolVersion: BILLING_PROTOCOL_VERSION,
      inspectionId,
      clientCreatedAt: numOrNull(raw.clientCreatedAt),
      offline: raw.offline === true,
      device
    }
  };
}

export function checkBillingResponse(raw: unknown): BillingResponse | null {
  if (!isRec(raw) || raw.protocolVersion !== BILLING_PROTOCOL_VERSION) return null;
  if (raw.ok === false) {
    const code = typeof raw.code === 'string' ? (raw.code as BillingErrorCode) : 'internalError';
    if (!(code in BILLING_ERROR_STATUS)) return null;
    return { ok: false, protocolVersion: BILLING_PROTOCOL_VERSION, code, message: typeof raw.message === 'string' ? raw.message : '' };
  }
  if (raw.ok !== true) return null;
  const status = checkBillingStatus(raw.status);
  if (!status) return null;
  const answer: BillingOkResponse = { ok: true, protocolVersion: BILLING_PROTOCOL_VERSION, status };
  if (isInspectionCoverage(raw.coverage)) answer.coverage = raw.coverage;
  if (typeof raw.inspectionId === 'string') answer.inspectionId = raw.inspectionId;
  if (isRec(raw.redeem) && typeof raw.redeem.outcome === 'string' && (REDEEM_OUTCOMES as readonly string[]).includes(raw.redeem.outcome)) {
    answer.redeem = { outcome: raw.redeem.outcome as RedeemOutcome, units: clampPositive(num(raw.redeem.units)) };
  }
  if (isRec(raw.release) && typeof raw.release.outcome === 'string' && (RELEASE_OUTCOMES as readonly string[]).includes(raw.release.outcome)) {
    answer.release = { outcome: raw.release.outcome as ReleaseOutcome, coverage: isInspectionCoverage(raw.release.coverage) ? raw.release.coverage : null };
  }
  if (isRec(raw.hold) && typeof raw.hold.outcome === 'string' && (HOLD_OUTCOMES as readonly string[]).includes(raw.hold.outcome)) {
    answer.hold = { outcome: raw.hold.outcome as HoldOutcome };
  }
  if (typeof raw.closed === 'boolean') answer.closed = raw.closed;
  return answer;
}
